import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { RotatingLines } from 'react-loader-spinner';
import { X, Upload } from 'lucide-react';
import './VirtualMallDashboard.scss';
import VirtualMallActivation from './VirtualMallActivation';
import VirtualMallSingleIdProducts from './VirtualMallSingleIdProducts';
import VirtualMallSingleIdsTree from './VirtualMallSingleIdsTree';
import VirtualMallWelcomeCertificate from './VirtualMallWelcomeCertificate';
import VirtualMallIncentive from './VirtualMallIncentive';
import VirtualMallTeam from './VirtualMallTeam';
import VirtualMallPurchase from './VirtualMallPurchase';
import VirtualMallPointPurchaseHistory from './VirtualMallPointPurchaseHistory';
import VirtualMallProfile from './VirtualMallProfile';
import VirtualMallChangePassword from './VirtualMallChangePassword';
import VirtualMallWithdraw from './VirtualMallWithdraw';
import VirtualMallUnknownUsers from './VirtualMallUnknownUsers';
import VirtualMallChat from './VirtualMallChat';
import Wahtsappstatus from '../Wahtsappstatus/Wahtsappstatus';
import APP_BASE_URL from '../../components/Api/globalApi';
import axios from "axios";
import { useNavigate } from 'react-router-dom';
import { virtualMallPurchaseApi, virtualMallUserTreeApi } from './VirtualMallApi/api.js';
import { imgBaseurl } from '../../baseUrl/baseurl.ts';

export default function VirtualMallDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('activation');
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    referralId: '',
    phone: '',
    joinDate: '',
    status: ''
  });
  const [isGeneratedSingleId, setIsGeneratedSingleId] = useState(false);
  const canAccessSingleIdTools =
    userData.referralId?.trim().toUpperCase() === 'SWVM001' || isGeneratedSingleId;
  const [userId, setUserId] = useState("");
  const [userApiKey, setUserApiKey] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purchaseData, setPurchaseData] = useState({
    name: '',
    address: '',
    transaction_id: '',
    image: null
  });
  const [purchaseTransactionCheck, setPurchaseTransactionCheck] = useState({ value: '', state: 'idle', message: '' });
  const [showTokenExpiredModal, setShowTokenExpiredModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  // Intercept back button only on activation tab (Removed)

  useEffect(() => {
    const handleTokenExpired = () => {
      setShowTokenExpiredModal(true);
    };

    window.addEventListener('virtualMallTokenExpired', handleTokenExpired);

    return () => {
      window.removeEventListener('virtualMallTokenExpired', handleTokenExpired);
    };
  }, []);

  useEffect(() => {
    const virtualReferalId = localStorage.getItem("virtual_referal_id");
    const virtualUserId = localStorage.getItem("virtual_user_id");
    const virtualName = localStorage.getItem("virtual_name");
    const storedUserId = localStorage.getItem("user_id");
    const storedUserApiKey = localStorage.getItem("user_api_key");

    if (virtualUserId) setUserId(virtualUserId);
    if (storedUserId) setUserId(storedUserId);
    if (storedUserApiKey) setUserApiKey(storedUserApiKey);

    setUserData(prev => ({
      ...prev,
      referralId: virtualReferalId || prev.referralId,
      name: virtualName || prev.name
    }));

    let active = true;
    virtualMallUserTreeApi({ levels: '1' })
      .then(response => {
        if (!active) return;
        const isGeneratedId = Boolean(response?.data?.is_single_id_generated);
        setIsGeneratedSingleId(isGeneratedId);

        const referralId = (virtualReferalId || '').trim().toUpperCase();
        if (isGeneratedId && referralId && referralId !== 'SWVM001') {
          let validity = null;
          try {
            validity = JSON.parse(localStorage.getItem('virtual_single_id_validity') || 'null');
          } catch (error) {
            validity = null;
          }
          if (validity?.show_expiry_notice) {
            const daysRemaining = Number(validity.days_until_block || 0);
            const notice = `This Single ID product has expired. Repurchase within ${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} to keep this ID active; it will be blocked after the grace period.`;

            toast.warning(({ closeToast }) => (
              <div>
                <div>{notice}</div>
                <button
                  type="button"
                  onClick={closeToast}
                  style={{ marginTop: '8px', border: 0, borderRadius: '4px', padding: '5px 12px', cursor: 'pointer' }}
                >
                  Skip and continue
                </button>
              </div>
            ), { autoClose: false });
          }
        }
      })
      .catch(error => {
        console.error('Could not determine Single ID account access:', error);
      });
    return () => { active = false; };
  }, []);

  const handleBuyProduct = (product) => {
    setSelectedProduct(product);
    setShowPurchaseModal(true);
  };

  const handleClosePurchaseModal = () => {
    setShowPurchaseModal(false);
    setSelectedProduct(null);
    setPurchaseData({
      product_id: '',
      amount: '',
      address: '',
      pincode: '',
      state: '',
      district: '',
      taluk: '',
      corporation: '',
      panchayat: '',
      mobile: '',
      transaction_id: '',
      image: null,
      name: '',
    });
    setPurchaseTransactionCheck({ value: '', state: 'idle', message: '' });
  };

  const handlePurchaseInputChange = (field) => (e) => {
    let value = e.target.value;

    // Strict validation for transaction_id
    if (field === 'transaction_id') {
      // Remove any non-numeric characters
      value = value.replace(/[^0-9]/g, '');
      // Limit to 12 characters
      if (value.length > 12) {
        return; // Do not update state if length exceeds 12
      }
      setPurchaseTransactionCheck({ value, state: 'idle', message: '' });
    }

    setPurchaseData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const checkPurchaseTransactionId = async () => {
    const transactionId = purchaseData.transaction_id;
    if (!/^\d{12}$/.test(transactionId)) {
      setPurchaseTransactionCheck({ value: transactionId, state: 'idle', message: '' });
      return 'invalid';
    }
    setPurchaseTransactionCheck({ value: transactionId, state: 'checking', message: 'Checking transaction ID…' });
    try {
      const response = await axios.post(
        `${APP_BASE_URL}/virtualuser/purchase/check-transaction-id`,
        { transaction_id: transactionId },
        { headers: { Authorization: `Bearer ${localStorage.getItem('virtualmall_token')}` } }
      );
      if (response.data?.status !== true) throw new Error(response.data?.message || 'Could not validate transaction ID');
      const result = response.data?.exists ? 'duplicate' : 'available';
      setPurchaseTransactionCheck({ value: transactionId, state: result, message: response.data?.message || (result === 'duplicate' ? 'Transaction ID already exists' : 'Transaction ID is available') });
      return result;
    } catch (error) {
      setPurchaseTransactionCheck({ value: transactionId, state: 'error', message: 'Could not check this ID. Please try again.' });
      return 'error';
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPurchaseData(prev => ({
        ...prev,
        transactionScreenshot: file
      }));
    }
  };

  const handlePurchaseSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!purchaseData.mobile || !/^\d{10}$/.test(purchaseData.mobile)) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }
    if (!purchaseData.pincode || !/^\d{6}$/.test(purchaseData.pincode)) {
      toast.error('Please enter a valid 6-digit pincode');
      return;
    }
    if (!purchaseData.state || !purchaseData.district || !purchaseData.taluk) {
      toast.error('Please fill in all address fields');
      return;
    }
    if (!purchaseData.transaction_id || !/^\d{12}$/.test(purchaseData.transaction_id)) {
      toast.error('Transaction ID must be exactly 12 digits');
      return;
    }
    if (purchaseTransactionCheck.value !== purchaseData.transaction_id || purchaseTransactionCheck.state !== 'available') {
      const transactionValidation = await checkPurchaseTransactionId();
      if (transactionValidation !== 'available') {
        toast.error(transactionValidation === 'duplicate'
          ? 'Transaction ID already exists. Please enter a different ID.'
          : 'Please check the transaction ID before submitting.');
        return;
      }
    }

    console.log('Purchase Data:', purchaseData);
    console.log('Product:', selectedProduct);
    const formData = new FormData();
    formData.append('product_id', selectedProduct.id || ''); // Assuming product has an ID
    formData.append('amount', selectedProduct.price || '');
    formData.append('name', purchaseData.name);
    formData.append('mobile', purchaseData.mobile);
    formData.append('address', purchaseData.address);
    formData.append('pincode', purchaseData.pincode);
    formData.append('state', purchaseData.state);
    formData.append('district', purchaseData.district);
    formData.append('taluk', purchaseData.taluk);
    formData.append('corporation', purchaseData.corporation);
    formData.append('panchayat', purchaseData.panchayat);
    formData.append('transaction_id', purchaseData.transaction_id); // Fixed lowercase
    formData.append('image', purchaseData.transactionScreenshot);

    try {
      setLoading(true);
      const response = await virtualMallPurchaseApi(formData);
      if (response.status === true) {
        toast.success(`Purchase order submitted for ${selectedProduct.name}!`);
        handleClosePurchaseModal();
      } else if (response.status === false) {
        toast.error(response.message || 'Purchase failed');
      }
      else {
        toast.error(response.message || 'Purchase failed');
      }
    } catch (error) {
      console.error("Purchase error:", error);
      if (error.response?.data?.message?.toLowerCase().includes("transaction id already exists")) {
        toast.error(error.response.data.message);
      }
      else {
        toast.error('An error occurred during purchase');
      }
    } finally {
      setLoading(false);
    }
  };

  const logOut = async () => {
    try {
      const response = await axios.post(
        `${APP_BASE_URL}/user/logout`,
        {},
        {
          headers: {
            user_id: userId,
            api_key: userApiKey,
          },
        }
      );
      if (response.data.result === true) {
        // localStorage.removeItem("referral_id");
        // localStorage.removeItem("user_name");
        // localStorage.removeItem("user_id");
        // localStorage.removeItem("user_api_key");
        localStorage.clear();
        setIsChecked(false);
        navigate("/");
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="virtual-mall-dashboard">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="sidebar-header">
          <div className="user-info">
            <div className="user-avatar">{userData?.name?.charAt(0)?.toUpperCase()}</div>
            <div className="user-details">
              <div className="user-name">{userData.name}</div>
              <div className="user-id">ID: {userData.referralId}</div>
            </div>
          </div>
        </div>

        <nav className="sidebar-nav">
          <button
            className={`nav-item ${activeTab === 'activation' ? 'active' : ''}`}
            onClick={() => setActiveTab('activation')}
          >
            <span className="nav-icon">🔗</span>
            <span className="nav-text">Activation</span>
          </button>
          {canAccessSingleIdTools && (
            <>
              <button
                className={`nav-item ${activeTab === 'single-id-products' ? 'active' : ''}`}
                onClick={() => setActiveTab('single-id-products')}
              >
                <span className="nav-icon">🛍️</span>
                <span className="nav-text">Single ID Products</span>
              </button>
              <button
                className={`nav-item ${activeTab === 'single-ids-tree' ? 'active' : ''}`}
                onClick={() => setActiveTab('single-ids-tree')}
              >
                <span className="nav-icon">🌳</span>
                <span className="nav-text">Single IDs Tree</span>
              </button>
              <button
                className={`nav-item ${activeTab === 'welcome-certificate' ? 'active' : ''}`}
                onClick={() => setActiveTab('welcome-certificate')}
              >
                <span className="nav-icon">📜</span>
                <span className="nav-text">Welcome Certificate</span>
              </button>
            </>
          )}
          <button
            className={`nav-item ${activeTab === 'incentive' ? 'active' : ''}`}
            onClick={() => setActiveTab('incentive')}
          >
            <span className="nav-icon">💰</span>
            <span className="nav-text">Incentive</span>
          </button>
          <button
            className={`nav-item ${activeTab === 'withdraw' ? 'active' : ''}`}
            onClick={() => setActiveTab('withdraw')}
          >
            <span className="nav-icon">💰</span>
            <span className="nav-text">Withdraw</span>
          </button>
          <button
            className={`nav-item ${activeTab === 'team' ? 'active' : ''}`}
            onClick={() => setActiveTab('team')}
          >
            <span className="nav-icon">👥</span>
            <span className="nav-text">Team</span>
          </button>
          <button
            className={`nav-item ${activeTab === 'unknown-users' ? 'active' : ''}`}
            onClick={() => setActiveTab('unknown-users')}
          >
            <span className="nav-icon">👥</span>
            <span className="nav-text">Unknown Users</span>
          </button>
          <button
            className={`nav-item ${activeTab === 'Purchase' ? 'active' : ''}`}
            onClick={() => setActiveTab('Purchase')}
          >
            <span className="nav-icon">🛍️</span>
            <span className="nav-text">Purchase</span>
          </button>
          <button
            className={`nav-item ${activeTab === 'point-purchase-history' ? 'active' : ''}`}
            onClick={() => setActiveTab('point-purchase-history')}
          >
            <span className="nav-icon">📊</span>
            <span className="nav-text">Virtual Mall Purchase</span>
          </button>
          <button
            className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            <span className="nav-icon">👤</span>
            <span className="nav-text">Profile</span>
          </button>
          <button
            className={`nav-item ${activeTab === 'chat' ? 'active' : ''}`}
            onClick={() => setActiveTab('chat')}
          >
            <span className="nav-icon">💬</span>
            <span className="nav-text">Chat with Admin</span>
          </button>
          <button
            className={`nav-item ${activeTab === 'whatsapp-status' ? 'active' : ''}`}
            onClick={() => setActiveTab('whatsapp-status')}
          >
            <span className="nav-icon">📸</span>
            <span className="nav-text">WhatsApp Status Image</span>
          </button>
          <button
            className={`nav-item ${activeTab === 'change-password' ? 'active' : ''}`}
            onClick={() => setActiveTab('change-password')}
          >
            <span className="nav-icon">🔒</span>
            <span className="nav-text">Change Password</span>
          </button>
        </nav>

        {activeTab === 'activation' && (
          <div className="sidebar-footer">
            <button onClick={() => setShowLogoutModal(true)} className="logout-btn">
              <span className="logout-icon">🚪</span>
              <span>Logout</span>
            </button>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Header */}
        {activeTab !== 'chat' && (
          <header className="main-header">
            <div className="header-left">
              <h1>{activeTab === 'point-purchase-history' ? 'Virtual Mall Purchase' : activeTab === 'single-id-products' ? 'Single ID Products' : activeTab === 'single-ids-tree' ? 'Single IDs Tree' : activeTab === 'welcome-certificate' ? 'Welcome Certificate' : activeTab.charAt(0).toUpperCase() + activeTab.slice(1).replace('-', ' ')}</h1>
              <div className="breadcrumb">Dashboard / {activeTab === 'point-purchase-history' ? 'Virtual Mall Purchase' : activeTab === 'single-id-products' ? 'Single ID Products' : activeTab === 'single-ids-tree' ? 'Single IDs Tree' : activeTab === 'welcome-certificate' ? 'Welcome Certificate' : activeTab.charAt(0).toUpperCase() + activeTab.slice(1).replace('-', ' ')}</div>
            </div>
          </header>
        )}

        {/* Dashboard Content */}
        <div className="dashboard-content">
          {activeTab === 'activation' && <VirtualMallActivation onBuyProduct={handleBuyProduct} />}
          {canAccessSingleIdTools && activeTab === 'single-id-products' && <VirtualMallSingleIdProducts />}
          {canAccessSingleIdTools && activeTab === 'single-ids-tree' && <VirtualMallSingleIdsTree />}
          {canAccessSingleIdTools && activeTab === 'welcome-certificate' && <VirtualMallWelcomeCertificate />}
          {activeTab === 'incentive' && <VirtualMallIncentive canViewShopPurchaseIncentive={canAccessSingleIdTools} />}
          {activeTab === 'team' && <VirtualMallTeam />}
          {activeTab === 'Purchase' && <VirtualMallPurchase />}
          {activeTab === 'point-purchase-history' && <VirtualMallPointPurchaseHistory />}
          {activeTab === 'profile' && <VirtualMallProfile />}
          {activeTab === 'chat' && <VirtualMallChat />}
          {activeTab === 'whatsapp-status' && <Wahtsappstatus />}
          {activeTab === 'change-password' && <VirtualMallChangePassword />}
          {activeTab === 'withdraw' && <VirtualMallWithdraw />}
          {activeTab === 'unknown-users' && <VirtualMallUnknownUsers />}
        </div>
      </div>

      {/* Token Expired Modal */}
      {showTokenExpiredModal && (
        <div className="vm-modal-overlay">
          <div className="vm-modal-content" style={{ maxWidth: '400px' }}>
            <div className="vm-modal-header">
              <h2>Session Expired</h2>
            </div>
            <div className="vm-modal-body">
              <p style={{ marginBottom: '20px', textAlign: 'center' }}>Your session has expired. Please log in again to continue.</p>
              <div className="vm-modal-actions" style={{ justifyContent: 'center' }}>
                <button
                  className="primary-btn"
                  onClick={() => {
                    localStorage.clear();
                    navigate("/");
                  }}
                >
                  Login Again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="vm-modal-overlay" onClick={() => setShowLogoutModal(false)}>
          <div className="vm-modal-content logout-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vm-modal-header">
              <h2>Confirm Logout</h2>
              <button className="vm-modal-close" onClick={() => setShowLogoutModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="vm-modal-body">
              <div className="logout-warning">
                <span className="warning-icon">⚠️</span>
                <p>Are you sure you want to logout?</p>
              </div>
              <div className="vm-modal-actions">
                <button
                  className="primary-btn logout-confirm-btn"
                  onClick={() => {
                    setShowLogoutModal(false);
                    logOut();
                  }}
                >
                  Yes, Logout
                </button>
                <button
                  className="secondary-btn"
                  onClick={() => setShowLogoutModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Modal */}
      {/* {showPurchaseModal && selectedProduct && (
        <div className="modal-overlay" onClick={handleClosePurchaseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Purchase {selectedProduct.name}</h2>
              <button className="modal-close" onClick={handleClosePurchaseModal}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <div className="product-summary">
                <img src={`${imgBaseurl}/${selectedProduct.image}`} alt={selectedProduct.name} className="modal-product-image" />
                <div className="modal-product-info">
                  <h3>{selectedProduct.name}</h3>
                  <p>{selectedProduct.description}</p>
                  <div className="modal-price">Price: ₹{Number(selectedProduct.price).toFixed(2)}</div>
                </div>
              </div>

              <form className="purchase-form" onSubmit={handlePurchaseSubmit}>
                <div className="form-group">
                  <label htmlFor="purchase-name">Full Name *</label>
                  <input
                    id="purchase-name"
                    type="text"
                    className="form-input"
                    placeholder="Enter your full name"
                    value={purchaseData.name}
                    onChange={handlePurchaseInputChange('name')}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="purchase-mobile">Mobile Number *</label>
                  <input
                    id="purchase-mobile"
                    type="tel"
                    className="form-input"
                    placeholder="Enter 10-digit mobile number"
                    value={purchaseData.mobile}
                    onChange={handlePurchaseInputChange('mobile')}
                    maxLength="10"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="purchase-address">Address *</label>
                  <textarea
                    id="purchase-address"
                    className="form-input form-textarea"
                    placeholder="Enter your complete address"
                    value={purchaseData.address}
                    onChange={handlePurchaseInputChange('address')}
                    rows="3"
                    required
                  />
                </div>

                <div className="form-group-row three-col">
                  <div className="form-group">
                    <label htmlFor="purchase-pincode">Pincode *</label>
                    <input
                      id="purchase-pincode"
                      type="text"
                      className="form-input"
                      placeholder="6-digit Pincode"
                      value={purchaseData.pincode}
                      onChange={handlePurchaseInputChange('pincode')}
                      maxLength="6"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="purchase-state">State *</label>
                    <input
                      id="purchase-state"
                      type="text"
                      className="form-input"
                      placeholder="State"
                      value={purchaseData.state}
                      onChange={handlePurchaseInputChange('state')}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="purchase-district">District *</label>
                    <input
                      id="purchase-district"
                      type="text"
                      className="form-input"
                      placeholder="District"
                      value={purchaseData.district}
                      onChange={handlePurchaseInputChange('district')}
                      required
                    />
                  </div>
                </div>

                <div className="form-group-row three-col">
                  <div className="form-group">
                    <label htmlFor="purchase-taluk">Taluk *</label>
                    <input
                      id="purchase-taluk"
                      type="text"
                      className="form-input"
                      placeholder="Taluk"
                      value={purchaseData.taluk}
                      onChange={handlePurchaseInputChange('taluk')}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="purchase-corporation">Corporation</label>
                    <input
                      id="purchase-corporation"
                      type="text"
                      className="form-input"
                      placeholder="Corporation"
                      value={purchaseData.corporation}
                      onChange={handlePurchaseInputChange('corporation')}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="purchase-panchayat">Panchayat</label>
                    <input
                      id="purchase-panchayat"
                      type="text"
                      className="form-input"
                      placeholder="Panchayat"
                      value={purchaseData.panchayat}
                      onChange={handlePurchaseInputChange('panchayat')}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="transaction-id">Transaction ID *</label>
                  <input
                    id="transaction-id"
                    type="text"
                    className="form-input"
                    placeholder="Enter transaction ID"
                    value={purchaseData.transaction_id}
                    onChange={handlePurchaseInputChange('transaction_id')}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="transaction-screenshot">Transaction Screenshot *</label>
                  <div className="file-upload-container">
                    <input
                      id="transaction-screenshot"
                      type="file"
                      className="file-input-hidden"
                      accept="image/*"
                      onChange={handleFileChange}
                      required={!purchaseData.transactionScreenshot}
                    />
                    <label htmlFor="transaction-screenshot" className="file-upload-label">
                      <Upload className="upload-icon" size={24} />
                      <span>{purchaseData.transactionScreenshot ? 'Change File' : 'Click to Upload Screenshot'}</span>
                    </label>
                  </div>
                  {purchaseData.transactionScreenshot && (
                    <div className="file-preview">
                      <span className="file-name">📎 {purchaseData.transactionScreenshot.name}</span>
                    </div>
                  )}
                </div>

                <div className="modal-actions">
                  <button type="submit" className="primary-btn" disabled={loading}>
                    {loading ? (
                      <div className="btn-loader">
                        <RotatingLines
                          strokeColor="white"
                          strokeWidth="5"
                          animationDuration="0.75"
                          width="24"
                          visible={true}
                        />
                        Processing...
                      </div>
                    ) : (
                      'Submit Order'
                    )}
                  </button>
                  <button type="button" className="secondary-btn" onClick={handleClosePurchaseModal} disabled={loading}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )} */}

      {showPurchaseModal && selectedProduct && (
        <div className="vm-modal-overlay" onClick={handleClosePurchaseModal}>
          <div className="vm-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="vm-modal-header">
              <h2>Purchase {selectedProduct.name}</h2>
              <button className="vm-modal-close" onClick={handleClosePurchaseModal}>
                <X size={24} />
              </button>
            </div>
            <div className="vm-modal-body">
              {/* Left Side - Product Summary */}
              <div className="product-summary">
                <div className="product-image-container">
                  <img
                    src={`${imgBaseurl}/${selectedProduct.image}`}
                    alt={selectedProduct.name}
                    className="vm-modal-product-image"
                  />
                </div>
                <div className="vm-modal-product-info">
                  <h3>{selectedProduct.name}</h3>
                  <p>{selectedProduct.description}</p>
                  <div className="vm-modal-price">₹{Number(selectedProduct.price).toFixed(2)}</div>
                </div>
              </div>

              {/* Right Side - Purchase Form */}
              <form className="purchase-form" onSubmit={handlePurchaseSubmit}>
                <div className="form-group">
                  <label htmlFor="purchase-name">Full Name *</label>
                  <input
                    id="purchase-name"
                    type="text"
                    className="form-input"
                    placeholder="Enter your full name"
                    value={purchaseData.name}
                    onChange={handlePurchaseInputChange('name')}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="purchase-mobile">Mobile Number *</label>
                  <input
                    id="purchase-mobile"
                    type="tel"
                    className="form-input"
                    placeholder="Enter 10-digit mobile number"
                    value={purchaseData.mobile}
                    onChange={handlePurchaseInputChange('mobile')}
                    maxLength="10"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="purchase-address">Address *</label>
                  <textarea
                    id="purchase-address"
                    className="form-input form-textarea"
                    placeholder="Enter your complete address"
                    value={purchaseData.address}
                    onChange={handlePurchaseInputChange('address')}
                    rows="3"
                    required
                  />
                </div>

                <div className="form-group-row three-col">
                  <div className="form-group">
                    <label htmlFor="purchase-pincode">Pincode *</label>
                    <input
                      id="purchase-pincode"
                      type="text"
                      className="form-input"
                      placeholder="6-digit Pincode"
                      value={purchaseData.pincode}
                      onChange={handlePurchaseInputChange('pincode')}
                      maxLength="6"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="purchase-state">State *</label>
                    <input
                      id="purchase-state"
                      type="text"
                      className="form-input"
                      placeholder="State"
                      value={purchaseData.state}
                      onChange={handlePurchaseInputChange('state')}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="purchase-district">District *</label>
                    <input
                      id="purchase-district"
                      type="text"
                      className="form-input"
                      placeholder="District"
                      value={purchaseData.district}
                      onChange={handlePurchaseInputChange('district')}
                      required
                    />
                  </div>
                </div>

                <div className="form-group-row three-col">
                  <div className="form-group">
                    <label htmlFor="purchase-taluk">Taluk *</label>
                    <input
                      id="purchase-taluk"
                      type="text"
                      className="form-input"
                      placeholder="Taluk"
                      value={purchaseData.taluk}
                      onChange={handlePurchaseInputChange('taluk')}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="purchase-corporation">Corporation</label>
                    <input
                      id="purchase-corporation"
                      type="text"
                      className="form-input"
                      placeholder="Corporation"
                      value={purchaseData.corporation}
                      onChange={handlePurchaseInputChange('corporation')}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="purchase-panchayat">Panchayat</label>
                    <input
                      id="purchase-panchayat"
                      type="text"
                      className="form-input"
                      placeholder="Panchayat"
                      value={purchaseData.panchayat}
                      onChange={handlePurchaseInputChange('panchayat')}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="transaction-id">Transaction ID *</label>
                  <input
                    id="transaction-id"
                    type="text"
                    className="form-input"
                    placeholder="Enter 12-digit transaction ID"
                    value={purchaseData.transaction_id}
                    onChange={handlePurchaseInputChange('transaction_id')}
                    onBlur={checkPurchaseTransactionId}
                    maxLength="12"
                    required
                  />
                  {purchaseTransactionCheck.message && purchaseTransactionCheck.value === purchaseData.transaction_id && <small className={`single-id-transaction-check is-${purchaseTransactionCheck.state}`} role="status">{purchaseTransactionCheck.message}</small>}
                </div>

                <div className="form-group">
                  <label htmlFor="transaction-screenshot">Transaction Screenshot *</label>
                  <div className="file-upload-container">
                    <input
                      id="transaction-screenshot"
                      type="file"
                      className="file-input-hidden"
                      accept="image/*"
                      onChange={handleFileChange}
                      required={!purchaseData.transactionScreenshot}
                    />
                    <label htmlFor="transaction-screenshot" className="file-upload-label">
                      <Upload className="upload-icon" size={24} />
                      <span>{purchaseData.transactionScreenshot ? 'Change File' : 'Click to Upload Screenshot'}</span>
                    </label>
                  </div>
                  {purchaseData.transactionScreenshot && (
                    <div className="file-preview">
                      <span className="file-name">📎 {purchaseData.transactionScreenshot.name}</span>
                    </div>
                  )}
                </div>

                <div className="vm-modal-actions">
                  <button type="submit" className="primary-btn" disabled={loading}>
                    {loading ? (
                      <div className="btn-loader">
                        <RotatingLines
                          strokeColor="white"
                          strokeWidth="5"
                          animationDuration="0.75"
                          width="24"
                          visible={true}
                        />
                        Processing...
                      </div>
                    ) : (
                      'Submit Order'
                    )}
                  </button>
                  <button type="button" className="secondary-btn" onClick={handleClosePurchaseModal} disabled={loading}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
